import React, { memo, ReactElement, useMemo } from 'react'

import 'ui/uiConfig/components/index'
import { useUiConfig } from 'api/uiConfig/uiConfig.api'
import { Moment } from 'moment'
import { ErrorBoundary } from 'react-error-boundary'
import authStore from 'store/auth/auth'
import { LoadingPlaceholderContainer } from 'ui/atoms'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import ErrorFallback from 'ui/components/ErrorFallback/ErrorFallback'
import UiConfigComponent from 'ui/uiConfig/UiConfigComponent'
import Datetime from 'utils/datetime/datetime'
import sentry from 'utils/sentry/sentry'
import { snapshot } from 'valtio'

type UiConfigProps = {
  type: string
  systemId?: number
  noWrappingDiv?: boolean
  datasetStartTime?: Moment | ISODateTime
  datasetEndTime?: Moment | ISODateTime
  fallback?: ReactElement
  tourId?: string
  ignoreZoom?: boolean
  overrideAlias?: UiConfigAliases
  datasetRefreshToken?: string
  datasetCreatedAt?: string
  datasetCreatedAtOperator?: DatasetCreatedAtOperators
}

function UiConfig({
  type,
  systemId,
  datasetStartTime,
  datasetEndTime,
  fallback,
  noWrappingDiv,
  tourId,
  ignoreZoom,
  overrideAlias,
  datasetRefreshToken,
  datasetCreatedAt,
  datasetCreatedAtOperator,
}: UiConfigProps): ReactElement | null {
  // Use snapshot() instead of useSnapshot() to avoid subscribing to entire store
  // We only need isBeta which changes very rarely
  const isBeta = snapshot(authStore).isBeta
  const { systemId: globalSystemId } = useAuth()
  const { data, isLoading } = useUiConfig(systemId || globalSystemId, type, { mode: isBeta ? `beta` : null })
  const uiConfig = data?.find((u) => u.component === type)

  // Memoize the ISO datetime conversions to prevent unnecessary recalculations
  const memoizedStartTime = useMemo(
    () => (datasetStartTime ? Datetime.toISOString(datasetStartTime) : undefined),
    [datasetStartTime]
  )
  const memoizedEndTime = useMemo(
    () => (datasetEndTime ? Datetime.toISOString(datasetEndTime) : undefined),
    [datasetEndTime]
  )

  if (isLoading) {
    return <LoadingPlaceholderContainer></LoadingPlaceholderContainer>
  }

  if (!data?.length || !uiConfig) {
    return fallback || <></>
  }

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={(error) => sentry.captureBoundaryException(error, { boundaryName: 'UiConfig' })}
    >
      <UiConfigComponent
        uid={uiConfig.uid}
        id={uiConfig.id}
        tourId={tourId}
        ignoreZoom={ignoreZoom}
        systemId={systemId || uiConfig.system}
        noWrappingDiv={noWrappingDiv}
        datasetStartTime={memoizedStartTime}
        datasetEndTime={memoizedEndTime}
        datasetCreatedAt={datasetCreatedAt}
        datasetCreatedAtOperator={datasetCreatedAtOperator}
        overrideAlias={overrideAlias}
        datasetRefreshToken={datasetRefreshToken}
      />
    </ErrorBoundary>
  )
}

export default memo(UiConfig)
